/**
 * Charge rows for an order's line-items table.
 *
 * Two table rows that hang under a parent line when it is expanded: an existing
 * charge, and the inline form that adds one. Shared by purchase orders and sales
 * orders, whose tables have different column counts — a PO has a receive
 * checkbox, a sales order has discount, cost and margin — so the surrounding
 * empty cells are parameterised rather than hardcoded. Getting those counts wrong
 * misaligns the row against its own header, which is exactly the sort of thing
 * that only shows up on screen.
 *
 * The add form's `is_taxable` follows the same discriminated-union rule as
 * `LineChargesSection`: required on the sales side, absent on the purchase side,
 * enforced by the type rather than by a runtime check.
 */

import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import Badge from "@/components/ui/Badge";
import {
  CHARGE_TYPES,
  chargeTypeLabel,
  DEFAULT_CHARGE_TYPE,
} from "@/components/ui/chargeTypes";
import type { LineChargeLike } from "@/components/ui/LineChargesSection";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);

/**
 * Where the charge row's content sits within the parent table's columns.
 *
 * `leading` and `trailing` are empty spacer cells; `contentColSpan` is how many
 * columns the description block spans; `filler` is the empty cells between the
 * description and the amount.
 */
export interface ChargeRowLayout {
  leading: number;
  contentColSpan: number;
  filler: number;
  trailing: number;
}

const spacers = (count: number, keyPrefix: string) =>
  Array.from({ length: count }, (_, i) => (
    <td key={`${keyPrefix}-${i}`} className="px-4 py-1.5" />
  ));

export function ChargeSubRow({
  charge,
  layout,
  canDelete,
  onDelete,
}: {
  charge: LineChargeLike;
  layout: ChargeRowLayout;
  canDelete: boolean;
  onDelete?: () => void;
}) {
  return (
    <tr className="border-b border-[var(--border-subtle)] bg-[var(--surface-sunken)]/30">
      {spacers(layout.leading, "lead")}
      <td
        className="px-4 py-1.5 text-xs text-[var(--text-secondary)]"
        colSpan={layout.contentColSpan}
      >
        <div className="flex items-center gap-2 pl-4">
          <span className="text-[var(--text-tertiary)]">└</span>
          <Badge variant={charge.tax_scheme_id ? "info" : "default"}>
            {charge.tax_scheme_id
              ? "scheme"
              : chargeTypeLabel(charge.charge_type)}
          </Badge>
          {charge.is_taxable && <Badge variant="warning">taxable</Badge>}
          <span>{charge.description}</span>
        </div>
      </td>
      {spacers(layout.filler, "fill")}
      <td className="px-4 py-1.5 text-right text-xs tabular-nums text-[var(--text-warning)]">
        + {fmt(Number(charge.amount))}
      </td>
      <td className="px-2 py-1.5">
        {canDelete && onDelete && (
          <button
            onClick={onDelete}
            /*
              A scheme's charge is recreated whenever schemes are applied, so the
              server refuses to delete it. Offering the button anyway would mean
              either a 400 the user has to interpret, or — worse, which is what
              used to happen at line level — a delete that appears to work and
              silently reverses, leaving someone believing a fee is waived.
            */
            disabled={!!charge.tax_scheme_id}
            title={
              charge.tax_scheme_id
                ? "Applied by a tax scheme — change the scheme's conditions to stop it applying here"
                : "Remove this charge"
            }
            className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--fill-danger)] hover:text-[var(--text-danger)] disabled:pointer-events-none disabled:opacity-30"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </td>
      {spacers(Math.max(layout.trailing - 1, 0), "trail")}
    </tr>
  );
}

interface NewCharge {
  charge_type: string;
  description: string;
  amount: number;
}

type AddChargeRowProps = {
  layout: ChargeRowLayout;
  isAdding: boolean;
} & (
  | { askTaxable?: false; onAdd: (data: NewCharge) => void }
  | {
      askTaxable: true;
      onAdd: (data: NewCharge & { is_taxable: boolean }) => void;
    }
);

export function AddChargeRow(props: AddChargeRowProps) {
  const { layout, isAdding, onAdd } = props;
  const askTaxable = props.askTaxable === true;

  const [chargeType, setChargeType] = useState(DEFAULT_CHARGE_TYPE);
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [taxable, setTaxable] = useState<boolean | null>(null);

  const amountValue = parseFloat(amount);
  const canAdd =
    !!desc.trim() &&
    !isNaN(amountValue) &&
    amountValue > 0 &&
    (!askTaxable || taxable !== null) &&
    !isAdding;

  const handleAdd = () => {
    if (!canAdd) return;
    const base = {
      charge_type: chargeType,
      description: desc.trim(),
      amount: amountValue,
    };
    if (askTaxable) {
      (onAdd as (d: NewCharge & { is_taxable: boolean }) => void)({
        ...base,
        is_taxable: taxable === true,
      });
    } else {
      (onAdd as (d: NewCharge) => void)(base);
    }
    setDesc("");
    setAmount("");
    setChargeType(DEFAULT_CHARGE_TYPE);
    setTaxable(null);
  };

  const cellInput =
    "h-6 rounded-[var(--radius-sm)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)]";

  return (
    <tr className="border-b border-[var(--border-subtle)] bg-[var(--surface-sunken)]/20">
      {spacers(layout.leading, "lead")}
      <td className="px-4 py-1.5" colSpan={layout.contentColSpan}>
        <div className="flex items-center gap-2 pl-4">
          <span className="text-[var(--text-tertiary)]">└</span>
          <select
            value={chargeType}
            onChange={(e) => setChargeType(e.target.value)}
            className={`${cellInput} cursor-pointer`}
          >
            {CHARGE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Charge description"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
            className={`${cellInput} flex-1`}
          />
          {askTaxable && (
            <select
              value={taxable === null ? "" : String(taxable)}
              onChange={(e) =>
                setTaxable(
                  e.target.value === "" ? null : e.target.value === "true",
                )
              }
              title="Whether this charge is itself taxed — it varies by state, so there is no default"
              className={`${cellInput} cursor-pointer ${
                taxable === null ? "text-[var(--text-tertiary)]" : ""
              }`}
            >
              <option value="">Taxable?</option>
              <option value="true">Taxable</option>
              <option value="false">Not taxable</option>
            </select>
          )}
        </div>
      </td>
      {spacers(layout.filler, "fill")}
      <td className="px-4 py-1.5 text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd();
          }}
          className={`${cellInput} w-24 [appearance:textfield] text-right tabular-nums [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
        />
      </td>
      <td className="px-2 py-1.5">
        <button
          onClick={handleAdd}
          disabled={!canAdd}
          className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--color-interactive-primary)] text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {isAdding ? (
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <Plus className="h-3 w-3" />
          )}
        </button>
      </td>
      {spacers(Math.max(layout.trailing - 1, 0), "trail")}
    </tr>
  );
}
